/**
 * LOGO COMPONENT - CONSISTENT BRANDING
 * ====================================
 * 
 * This component provides a consistent logo throughout the application.
 * It uses an inline SVG to ensure compatibility across all environments,
 * including Figma Make previews.
 * 
 * FEATURES:
 * - Responsive sizing based on size prop
 * - Proper alt text for accessibility
 * - Optional click handler for navigation
 * - Consistent styling across light/dark themes
 * - Inline SVG for maximum compatibility
 */

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';     // Size variants
  onClick?: (() => void) | undefined;                  // Optional click handler
  className?: string;                    // Additional CSS classes
  showText?: boolean;                    // Whether to show "Decisionr.xyz" text
}

// Inline SVG Logo Component
function LogoSVG({ className }: { className?: string }) {
  return (
    <svg 
      width="32" 
      height="32" 
      viewBox="0 0 1024 1024" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path 
        fill="currentColor" 
        opacity="1.00" 
        d=" M 244.86 148.76 C 249.94 146.56 255.42 145.48 260.94 145.42 C 341.97 145.30 422.99 145.88 504.01 145.64 C 559.35 145.49 614.69 145.55 670.02 145.00 C 692.36 145.01 714.69 144.98 737.03 145.01 C 747.54 145.46 758.36 147.17 767.66 152.38 C 781.08 160.20 792.25 172.46 797.77 187.08 C 803.45 201.69 802.77 217.63 802.55 233.01 C 801.63 286.69 802.19 340.38 801.89 394.06 C 801.75 518.39 801.74 642.72 802.00 767.06 C 801.78 778.74 802.96 790.64 799.83 802.06 C 793.27 829.03 766.59 848.92 739.06 849.00 C 719.61 848.99 700.17 849.08 680.72 849.00 C 680.52 860.66 680.72 872.33 680.65 884.00 C 680.57 886.63 680.31 889.44 678.79 891.69 C 676.52 894.21 672.66 895.96 669.37 894.58 C 655.76 884.96 642.53 874.51 628.96 865.04 C 615.06 874.41 601.73 884.65 587.68 893.82 C 585.52 895.45 582.73 895.28 580.19 895.03 C 578.89 893.59 577.16 892.18 577.06 890.10 C 576.67 876.40 576.87 862.70 577.02 849.00 C 475.35 848.99 373.69 849.00 272.02 849.01 C 260.85 849.17 249.99 844.58 241.70 837.23 C 228.76 826.48 220.31 810.06 220.59 793.08 C 221.34 763.40 220.74 733.70 221.22 704.01 C 221.83 537.67 221.80 371.33 221.00 204.99 C 221.33 194.29 219.95 183.38 222.74 172.89 C 224.87 161.57 233.91 152.04 244.86 148.76 M 318.02 178.06 C 318.06 314.39 317.98 450.73 318.00 587.06 C 317.96 640.38 318.22 693.70 317.49 747.01 C 317.41 748.97 317.57 750.94 318.00 752.87 C 324.61 753.95 331.34 753.38 338.02 753.63 C 370.35 753.80 402.68 753.90 435.01 753.93 C 484.64 753.87 534.29 754.34 583.91 753.03 C 616.94 752.98 649.98 752.98 683.01 753.00 C 701.03 752.84 719.06 753.91 737.07 752.94 C 746.00 751.58 754.84 747.91 760.93 741.04 C 767.66 733.81 770.13 723.64 770.01 713.99 C 769.99 546.99 770.01 379.99 769.99 212.99 C 769.67 198.35 760.31 183.68 745.93 179.40 C 734.82 176.82 723.33 177.65 712.02 177.49 C 655.65 177.57 599.28 177.53 542.91 177.99 C 467.95 177.96 392.99 178.10 318.02 178.06 M 753.12 782.73 C 745.20 785.01 736.98 785.12 728.83 785.66 C 713.23 785.93 697.61 785.34 682.03 786.03 C 681.79 786.28 681.30 786.78 681.06 787.02 C 680.38 794.32 680.95 801.68 680.72 809.00 C 680.87 811.23 680.32 813.68 681.31 815.77 C 682.74 816.59 684.42 816.61 686.03 816.64 C 699.35 816.61 712.67 816.57 725.99 816.56 C 731.71 816.51 737.46 816.79 743.14 815.91 C 753.80 813.99 763.26 806.09 766.73 795.78 C 769.24 789.25 768.77 782.16 769.67 775.34 C 763.97 777.40 759.23 781.75 753.12 782.73 M 255.92 788.06 C 255.18 794.83 254.15 802.62 258.72 808.37 C 263.87 815.22 273.02 816.58 281.01 817.02 C 379.67 817.21 478.33 816.99 576.99 816.99 C 577.00 806.67 577.01 796.34 576.99 786.01 C 533.64 785.65 490.29 785.50 446.95 785.62 C 391.97 785.77 337.00 785.55 282.02 785.61 C 273.94 785.44 265.82 786.01 257.79 784.94 C 255.86 784.33 256.15 786.96 255.92 788.06 Z" 
      />
      <path 
        fill="currentColor" 
        opacity="1.00" 
        d=" M 404.40 296.52 C 405.87 296.01 407.45 296.02 409.00 296.00 C 437.69 296.00 466.38 296.00 495.08 296.00 C 512.02 296.13 529.04 295.35 545.89 297.55 C 569.32 300.29 592.38 307.39 612.84 319.22 C 646.25 338.23 671.56 370.61 682.36 407.46 C 689.86 431.89 691.44 457.64 690.99 483.05 C 690.19 504.05 687.19 525.16 679.98 544.99 C 673.27 563.66 663.31 581.27 649.96 595.99 C 624.13 624.57 587.64 642.23 549.85 648.18 C 538.01 650.25 525.96 650.22 513.98 650.58 C 486.32 650.69 458.65 650.23 430.98 650.65 C 422.60 650.74 414.20 651.29 405.82 650.85 C 402.17 650.67 398.85 647.62 398.73 643.91 C 398.37 639.62 398.54 635.32 398.50 631.04 C 398.39 572.34 398.37 513.65 398.25 454.95 C 398.42 404.65 397.85 354.35 398.74 304.06 C 398.53 300.57 401.03 297.32 404.40 296.52 M 473.79 365.78 C 471.60 366.48 472.31 369.21 472.12 370.95 C 472.24 438.96 472.17 506.97 472.23 574.98 C 472.18 576.97 472.40 578.98 473.00 580.90 C 475.93 581.65 478.95 581.97 481.99 581.99 C 497.34 582.15 512.70 582.23 528.06 581.99 C 541.98 581.06 555.86 577.73 568.25 571.18 C 582.73 563.48 595.37 551.91 603.18 537.39 C 612.82 520.29 616.38 500.48 617.00 481.06 C 616.77 471.43 617.62 461.73 616.25 452.16 C 615.06 437.92 611.49 423.73 604.87 411.01 C 597.44 396.52 585.36 384.55 570.97 376.97 C 555.12 368.30 536.89 364.96 518.97 365.00 C 506.96 365.04 494.96 364.91 482.96 365.07 C 479.91 365.32 476.75 364.91 473.79 365.78 Z" 
      />
    </svg>
  );
}

export function Logo({ 
  size = 'md', 
  onClick, 
  className = '', 
  showText = true 
}: LogoProps) {
  
  // Size mappings for the logo image
  const sizeClasses = {
    sm: 'h-6 w-6',      // 24px - Small sidebar or mobile
    md: 'h-8 w-8',      // 32px - Standard header size
    lg: 'h-10 w-10',    // 40px - Larger headers
    xl: 'h-12 w-12'     // 48px - Hero sections or special cases
  };
  
  // Text size mappings to match logo size
  const textSizeClasses = {
    sm: 'text-lg',      // Small text
    md: 'text-xl',      // Standard text
    lg: 'text-2xl',     // Large text
    xl: 'text-3xl'      // Extra large text
  };
  
  const logoElement = (
    <div className={`flex items-center space-x-2 ${className} ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}>
      {/* Logo SVG */}
      <div className={`${sizeClasses[size]} flex-shrink-0`}>
        <LogoSVG className="w-full h-full text-foreground" />
      </div>
      
      {/* Brand Text */}
      {showText && (
        <span className={`font-medium text-foreground ${textSizeClasses[size]} leading-none`}>
          Decisionr
        </span>
      )}
    </div>
  );
  
  // If onClick is provided, make it clickable
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="flex items-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg p-1 -m-1"
        aria-label="Decisionr.xyz Home"
      >
        {logoElement}
      </button>
    );
  }
  
  // Otherwise, just return the logo element
  return logoElement;
}

/**
 * LOGO ICON ONLY COMPONENT
 * ========================
 * For cases where we only want the logo without text
 */
export function LogoIcon({ 
  size = 'md', 
  onClick, 
  className = '' 
}: Omit<LogoProps, 'showText'>) {
  return (
    <Logo 
      size={size} 
      onClick={onClick} 
      className={className} 
      showText={false} 
    />
  );
}

/**
 * USAGE EXAMPLES:
 * ==============
 * 
 * // Standard logo with text
 * <Logo size="md" onClick={() => navigateHome()} />
 * 
 * // Large logo for hero sections
 * <Logo size="xl" showText={true} />
 * 
 * // Icon only for compact spaces
 * <LogoIcon size="sm" onClick={() => navigateHome()} />
 * 
 * // Custom styling
 * <Logo size="lg" className="mb-4" showText={false} />
 */